import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Button,
  Flex,
  Text,
  Box,
  Link,
  Modal,
  ModalOverlay,
  useDisclosure,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useToast,
  HStack,
  IconButton,
  Divider,
  useColorModeValue,
  Select,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight, FiX } from "react-icons/fi";

import SearchPager from "../SearchPager";

import { booksItems } from "../DATABASE/publicationsBooks";
import { journalItems } from "../DATABASE/publicationsJournal";
import { conferenceItems } from "../DATABASE/publicationsConference";
import { chaptersItems } from "../DATABASE/publicationsChapters";

function matchesQuery(item, query, keys) {
  if (!query.trim()) return true;
  const q = query.toLowerCase();
  return keys.some((k) => String(item?.[k] ?? "").toLowerCase().includes(q));
}

function extractYearFromBib(bib) {
  if (!bib) return null;
  // matches: year={2024} OR year = {2024} OR year="2024" OR year = "2024"
  const m = String(bib).match(/year\s*=\s*[{"]\s*(\d{4})\s*[}"]/i);
  return m ? Number(m[1]) : null;
}

function extractYearFromText(text) {
  if (!text) return null;
  // fallback for theses or entries with no bib
  const m = String(text).match(/\b(19|20)\d{2}\b/);
  return m ? Number(m[0]) : null;
}

function getItemYear(item) {
  return extractYearFromBib(item?.bib) ?? extractYearFromText(item?.text);
}

function matchesYearRange(item, startYear, endYear) {
  // If both are empty => no filtering
  const hasStart = !!startYear && startYear !== "Any";
  const hasEnd = !!endYear && endYear !== "Any";
  if (!hasStart && !hasEnd) return true;

  const y = getItemYear(item);
  // If item has no year, hide it when a range is active
  if (!y) return false;

  const s = hasStart ? Number(startYear) : -Infinity;
  const e = hasEnd ? Number(endYear) : Infinity;
  return y >= s && y <= e;
}

function paginate(list, page, pageSize) {
  const totalPages = Math.max(1, Math.ceil(list.length / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    totalPages,
    safePage,
    pageItems: list.slice(start, start + pageSize),
  };
}

function scrollToId(id) {
  const target = document.getElementById(id);
  const offset = 85;
  if (target) {
    const top = target.getBoundingClientRect().top + window.pageYOffset - offset;
    window.scrollTo({ top, behavior: "smooth" });
  }
}

/**
 * UPDATED header block (same style you liked):
 * Row 1: Title (left) + Back to top (right)
 * Row 2: Pager UNDER title (left aligned)
 * - minimal bottom spacing so content starts close to title
 */
function SectionHeaderBlock({
  title,
  page,
  totalPages,
  onPrev,
  onNext,
  onBackToTop,
  uoRed = "#8F001A",
  uoRedHover = "#6E0014",
}) {
  return (
    <Box mb={2}>
      {/* Row 1 */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={2}>
        <Text
          fontSize="lg"
          fontWeight="800"
          textDecoration="underline"
          textUnderlineOffset="4px"
          textDecorationThickness="2px"
          lineHeight="1.2"
        >
          {title}
        </Text>

        <Link
          onClick={onBackToTop}
          fontSize="sm"
          fontWeight="600"
          color={uoRed}
          textDecoration="underline"
          textUnderlineOffset="3px"
          _hover={{ color: uoRedHover }}
          cursor="pointer"
          whiteSpace="nowrap"
        >
          Back to top
        </Link>
      </Flex>

      {/* Row 2 */}
      {totalPages > 1 && (
        <Flex mt={2} align="center" justify="flex-start" wrap="wrap" gap={2}>
          <HStack spacing={2} flexWrap="wrap">
            <IconButton
              aria-label="Previous page"
              icon={<FiChevronLeft />}
              onClick={onPrev}
              isDisabled={page <= 1}
              variant="outline"
              size="sm"
              borderRadius="999px"
            />
            <Text fontSize="sm" minW="88px" textAlign="center">
              Page{" "}
              <Text as="span" fontWeight="700">
                {page}
              </Text>{" "}
              / {totalPages}
            </Text>
            <IconButton
              aria-label="Next page"
              icon={<FiChevronRight />}
              onClick={onNext}
              isDisabled={page >= totalPages}
              variant="outline"
              size="sm"
              borderRadius="999px"
              _hover={{ borderColor: uoRed, color: uoRed }}
            />
          </HStack>
        </Flex>
      )}
    </Box>
  );
}

export default function PublicationList() {
  const uoRed = "#8F001A";
  const uoRedHover = "#6E0014";

  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const textColor = useColorModeValue("gray.700", "gray.300");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const sectionBg = useColorModeValue("white", "gray.800");

  const linkStyle = {
    color: uoRed,
    textDecoration: "underline",
    textUnderlineOffset: "3px",
    fontWeight: 600,
    _hover: { color: uoRedHover, textDecorationThickness: "2px" },
    cursor: "pointer",
  };

  // global search
  const [query, setQuery] = useState("");

  // ✅ year range (Any or "2024", etc.)
  const [startYear, setStartYear] = useState("Any");
  const [endYear, setEndYear] = useState("Any");

  // pagination
  const PAGE_SIZE = 1000000;
  const [pBooks, setPBooks] = useState(1);
  const [pJournals, setPJournals] = useState(1);
  const [pConfs, setPConfs] = useState(1);
  const [pChapters, setPChapters] = useState(1);
  const [pTheses, setPTheses] = useState(1);

  // bib modal
  const [activeBib, setActiveBib] = useState("");
  const [activeText, setActiveText] = useState("");

  const handleBibClick = (bib, text) => {
    setActiveBib(bib);
    setActiveText(text);
    onOpen();
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(activeBib).then(() => {
      toast({
        title: "Copied to your clipboard!",
        status: "success",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      });
    });
  };

  // keep range sane: if start > end, auto-fix by moving end
  useEffect(() => {
    const s = startYear !== "Any" ? Number(startYear) : null;
    const e = endYear !== "Any" ? Number(endYear) : null;
    if (s != null && e != null && s > e) {
      setEndYear(startYear);
    }
  }, [startYear, endYear]);

  // reset pages when query/year changes
  useEffect(() => {
    setPBooks(1);
    setPJournals(1);
    setPConfs(1);
    setPChapters(1);
    setPTheses(1);
  }, [query, startYear, endYear]);

  const searchActive =
    query.trim().length > 0 || startYear !== "Any" || endYear !== "Any";

  // theses as items (search/pagination)
  const thesesItems = useMemo(
    () => [
      {
        index: 1,
        text:
          'Ph.D Thesis: "Availability Constrained Routing and Wavelength Assignment and Survivability in Optical WDM Networks", Burak Kantarci, May 2009, Istanbul Technical University, Institute of Science and Technology, Computer Engineering Program. (Supervisors: Dr. Sema Oktug, ITU, and Dr. Hussein T. Mouftah, University of Ottawa).',
      },
      {
        index: 2,
        text:
          'M.Sc Thesis: "Performance Comparison and Traffic Analysis in Optical-Burst Switched Networks", Burak Kantarci, January 2005, Istanbul Technical University, Institute of Science and Technology, Computer Engineering Program. (Supervisor: Dr. Sema Oktug, ITU).',
      },
    ],
    []
  );

  // years list from all items
  const allItemsForYears = useMemo(() => {
    const thesesAsPub = thesesItems.map((t) => ({ ...t, bib: null }));
    return [
      ...booksItems,
      ...journalItems,
      ...conferenceItems,
      ...chaptersItems,
      ...thesesAsPub,
    ];
  }, [thesesItems]);

  const availableYears = useMemo(() => {
    const years = new Set();
    for (const it of allItemsForYears) {
      const y = getItemYear(it);
      if (y) years.add(y);
    }
    return Array.from(years).sort((a, b) => b - a); // newest first
  }, [allItemsForYears]);

  // filtered lists (keyword + year range)
  const booksFiltered = useMemo(
    () =>
      booksItems.filter(
        (x) =>
          matchesQuery(x, query, ["index", "text", "doi", "bib"]) &&
          matchesYearRange(x, startYear, endYear)
      ),
    [query, startYear, endYear]
  );

  const journalsFiltered = useMemo(
    () =>
      journalItems.filter(
        (x) =>
          matchesQuery(x, query, ["index", "text", "doi", "bib"]) &&
          matchesYearRange(x, startYear, endYear)
      ),
    [query, startYear, endYear]
  );

  const confsFiltered = useMemo(
    () =>
      conferenceItems.filter(
        (x) =>
          matchesQuery(x, query, ["index", "text", "doi", "bib"]) &&
          matchesYearRange(x, startYear, endYear)
      ),
    [query, startYear, endYear]
  );

  const chaptersFiltered = useMemo(
    () =>
      chaptersItems.filter(
        (x) =>
          matchesQuery(x, query, ["index", "text", "doi", "bib"]) &&
          matchesYearRange(x, startYear, endYear)
      ),
    [query, startYear, endYear]
  );

  const thesesFiltered = useMemo(
    () =>
      thesesItems.filter(
        (x) =>
          matchesQuery(x, query, ["text"]) &&
          matchesYearRange(x, startYear, endYear)
      ),
    [query, startYear, endYear, thesesItems]
  );

  // paging
  const booksPg = useMemo(
    () => paginate(booksFiltered, pBooks, PAGE_SIZE),
    [booksFiltered, pBooks]
  );
  const journalsPg = useMemo(
    () => paginate(journalsFiltered, pJournals, PAGE_SIZE),
    [journalsFiltered, pJournals]
  );
  const confsPg = useMemo(
    () => paginate(confsFiltered, pConfs, PAGE_SIZE),
    [confsFiltered, pConfs]
  );
  const chaptersPg = useMemo(
    () => paginate(chaptersFiltered, pChapters, PAGE_SIZE),
    [chaptersFiltered, pChapters]
  );
  const thesesPg = useMemo(
    () => paginate(thesesFiltered, pTheses, PAGE_SIZE),
    [thesesFiltered, pTheses]
  );

  // sections list (hide empty during search)
  const sections = useMemo(
    () => [
      { id: "books", label: "Books", total: booksItems.length, filtered: booksFiltered.length },
      {
        id: "journal-articles",
        label: "Refereed Journal Articles",
        total: journalItems.length,
        filtered: journalsFiltered.length,
      },
      {
        id: "conference-papers",
        label: "Refereed Conference/Workshop Papers",
        total: conferenceItems.length,
        filtered: confsFiltered.length,
      },
      {
        id: "book-chapters",
        label: "Book Chapters",
        total: chaptersItems.length,
        filtered: chaptersFiltered.length,
      },
      { id: "theses", label: "Theses", total: thesesItems.length, filtered: thesesFiltered.length },
    ],
    [
      booksFiltered.length,
      journalsFiltered.length,
      confsFiltered.length,
      chaptersFiltered.length,
      thesesFiltered.length,
      thesesItems.length,
    ]
  );

  const visibleSections = useMemo(() => {
    if (!searchActive) return sections;
    return sections.filter((s) => s.filtered > 0);
  }, [sections, searchActive]);

  const totalAll =
    booksItems.length +
    journalItems.length +
    conferenceItems.length +
    chaptersItems.length +
    thesesItems.length;

  const filteredAll =
    booksFiltered.length +
    journalsFiltered.length +
    confsFiltered.length +
    chaptersFiltered.length +
    thesesFiltered.length;

  const EntryRow = ({ item }) => (
    <Box mt={2} color={textColor} lineHeight="1.8">
      <Text as="span" fontWeight="800">
        {item.index}.
      </Text>{" "}
      <Text as="span">{item.text} </Text>
      {item.doi ? (
        <>
          {" "}
          <Link href={item.doi} isExternal {...linkStyle} color="blue">
            [doi]
          </Link>
        </>
      ) : null}
      {item.bib ? (
        <>
          {" "}
          <Link onClick={() => handleBibClick(item.bib, item.text)} {...linkStyle}>
            [bib]
          </Link>
        </>
      ) : null}
    </Box>
  );

  const commonSectionProps = {
    bg: sectionBg,
    border: `1px solid ${borderColor}`,
    borderRadius: "18px",
    px: { base: 3, md: 4 },
    pt: { base: 3, md: 4 },
    pb: { base: 2, md: 3 },
  };

  const hasFilters =
    query.trim().length > 0 || startYear !== "Any" || endYear !== "Any";

  return (
    <>
      {/* Header */}
      <Container maxW="1100px" py={6}>
        <Text fontSize={{ base: "26px", md: "32px" }} fontWeight="900" textAlign="center">
          Publications
        </Text>
        <Text textAlign="center" mt={2} fontSize="lg" color={textColor}>
          <Link href="/" {...linkStyle}>
            Dr. Burak Kantarci
          </Link>
        </Text>
        <Divider mt={6} />
      </Container>

      <Container maxW="1200px" pb={10}>
        {/* Navigation buttons (hide during search/filter) */}
        {!searchActive && (
          <Flex justify="center" mt={2} gap={2} wrap="wrap">
            {sections.map((s) => (
              <Button
                key={s.id}
                onClick={() => scrollToId(s.id)}
                variant="outline"
                borderRadius="999px"
                bg={useColorModeValue("white", "gray.900")}
                borderColor={borderColor}
                _hover={{
                  borderColor: uoRed,
                  color: uoRed,
                  bg: useColorModeValue("rgba(143,0,26,0.06)", "rgba(143,0,26,0.18)"),
                }}
                fontWeight="700"
                px={5}
              >
                {s.label}
              </Button>
            ))}
          </Flex>
        )}

        {/* --- HORIZONTAL TOOLBAR: SEARCH + YEAR FILTERS --- */}
        <Box mt={5}>
          <Flex
            direction={{ base: "column", md: "row" }}
            gap={4}
            align={{ base: "stretch", md: "flex-start" }} // ✅ align top edges
          >
            {/* SEARCH BAR (Grows to fill space) */}
            <Box flex="1" w="full">
              <SearchPager
                mode="search"
                title="Search Publications"
                query={query}
                onQueryChange={setQuery}
                page={1}
                pageSize={PAGE_SIZE}
                totalItems={totalAll}
                filteredItems={filteredAll}
                onPrev={() => {}}
                onNext={() => {}}
              />

              {/* YEAR RANGE (Horizontal) */}
              <HStack spacing={4} align="flex-end" wrap="wrap" mb={1} ml={5}>
                <Box>
                  <Text fontSize="xs" fontWeight="700" color={textColor} mb={1} ml={1}>
                    From Year
                  </Text>
                  <Select
                    value={startYear}
                    onChange={(e) => setStartYear(e.target.value)}
                    borderRadius="10px"
                    size="md"
                    bg={useColorModeValue("white", "gray.800")}
                    borderColor={borderColor}
                    minW="110px"
                  >
                    <option value="Any">Any</option>
                    {availableYears.map((y) => (
                      <option key={`s-${y}`} value={String(y)}>
                        {y}
                      </option>
                    ))}
                  </Select>
                </Box>

                <Box>
                  <Text fontSize="xs" fontWeight="700" color={textColor} mb={1} ml={1}>
                    To Year
                  </Text>
                  <Select
                    value={endYear}
                    onChange={(e) => setEndYear(e.target.value)}
                    borderRadius="10px"
                    size="md"
                    bg={useColorModeValue("white", "gray.800")}
                    borderColor={borderColor}
                    minW="110px"
                  >
                    <option value="Any">Any</option>
                    {availableYears
                      .slice()
                      .sort((a, b) => a - b)
                      .map((y) => (
                        <option key={`e-${y}`} value={String(y)}>
                          {y}
                        </option>
                      ))}
                  </Select>
                </Box>

                {hasFilters && (
                  <Button
                    onClick={() => {
                      setQuery("");
                      setStartYear("Any");
                      setEndYear("Any");
                    }}
                    variant="ghost"
                    colorScheme="red"
                    size="md"
                    borderRadius="10px"
                    aria-label="Clear filters"
                    leftIcon={<FiX />}
                  >
                    Clear
                  </Button>
                )}
              </HStack>
            </Box>
          </Flex>
        </Box>

        {/* Empty state */}
        {searchActive && visibleSections.length === 0 && (
          <Box mt={6} p={5} border={`1px solid ${borderColor}`} borderRadius="18px">
            <Text color="gray.500">No results found.</Text>
          </Box>
        )}

        {/* Sections */}
        {(!searchActive || booksFiltered.length > 0) && (
          <Box id="books" mt={5} {...commonSectionProps}>
            <SectionHeaderBlock
              title="Books"
              page={booksPg.safePage}
              totalPages={booksPg.totalPages}
              onPrev={() => setPBooks((p) => Math.max(1, p - 1))}
              onNext={() => setPBooks((p) => Math.min(booksPg.totalPages, p + 1))}
              onBackToTop={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              uoRed={uoRed}
              uoRedHover={uoRedHover}
            />
            {booksPg.pageItems.map((item, idx) => (
              <EntryRow key={`b-${idx}`} item={item} />
            ))}
          </Box>
        )}

        {(!searchActive || journalsFiltered.length > 0) && (
          <Box id="journal-articles" mt={5} {...commonSectionProps}>
            <SectionHeaderBlock
              title="Refereed Journal Articles"
              page={journalsPg.safePage}
              totalPages={journalsPg.totalPages}
              onPrev={() => setPJournals((p) => Math.max(1, p - 1))}
              onNext={() => setPJournals((p) => Math.min(journalsPg.totalPages, p + 1))}
              onBackToTop={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              uoRed={uoRed}
              uoRedHover={uoRedHover}
            />
            {journalsPg.pageItems.map((item, idx) => (
              <EntryRow key={`j-${idx}`} item={item} />
            ))}
          </Box>
        )}

        {(!searchActive || confsFiltered.length > 0) && (
          <Box id="conference-papers" mt={5} {...commonSectionProps}>
            <SectionHeaderBlock
              title="Refereed Conference/Workshop Papers"
              page={confsPg.safePage}
              totalPages={confsPg.totalPages}
              onPrev={() => setPConfs((p) => Math.max(1, p - 1))}
              onNext={() => setPConfs((p) => Math.min(confsPg.totalPages, p + 1))}
              onBackToTop={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              uoRed={uoRed}
              uoRedHover={uoRedHover}
            />
            {confsPg.pageItems.map((item, idx) => (
              <EntryRow key={`c-${idx}`} item={item} />
            ))}
          </Box>
        )}

        {(!searchActive || chaptersFiltered.length > 0) && (
          <Box id="book-chapters" mt={5} {...commonSectionProps}>
            <SectionHeaderBlock
              title="Book Chapters"
              page={chaptersPg.safePage}
              totalPages={chaptersPg.totalPages}
              onPrev={() => setPChapters((p) => Math.max(1, p - 1))}
              onNext={() => setPChapters((p) => Math.min(chaptersPg.totalPages, p + 1))}
              onBackToTop={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              uoRed={uoRed}
              uoRedHover={uoRedHover}
            />
            {chaptersPg.pageItems.map((item, idx) => (
              <EntryRow key={`ch-${idx}`} item={item} />
            ))}
          </Box>
        )}

        {(!searchActive || thesesFiltered.length > 0) && (
          <Box id="theses" mt={5} {...commonSectionProps}>
            <SectionHeaderBlock
              title="Theses"
              page={thesesPg.safePage}
              totalPages={thesesPg.totalPages}
              onPrev={() => setPTheses((p) => Math.max(1, p - 1))}
              onNext={() => setPTheses((p) => Math.min(thesesPg.totalPages, p + 1))}
              onBackToTop={() => window.scrollTo({ top: 0, behavior: "smooth" })}
              uoRed={uoRed}
              uoRedHover={uoRedHover}
            />

            {thesesPg.pageItems.map((t, idx) => (
              <Box key={`t-${idx}`} mt={2} textAlign="justify" color={textColor} lineHeight="1.8">
                <Text as="span">{t.text}</Text>
              </Box>
            ))}
          </Box>
        )}
      </Container>

      {/* Bib modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="800px" maxH="600px">
          <ModalHeader fontSize="lg">BibTeX Entry</ModalHeader>
          <ModalCloseButton />
          <ModalBody overflowY="auto">
            <Box fontSize="md" mb={3} fontWeight="600">
              {activeText}
            </Box>

            <Box fontFamily="monospace" whiteSpace="pre-wrap" fontSize="sm" lineHeight="1.4">
              {activeBib}
            </Box>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleCopy}>
              Copy
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}